
import { Book, ChevronDown, Menu, Sunset, Trees, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import CountdownBanner from "../CountdownBanner";

interface MenuItem {
  title: string;
  url: string;
  description?: string;
  icon?: JSX.Element;
  items?: MenuItem[];
  onClick?: () => void;
  external?: boolean;
}

interface Navbar1Props {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
    onClick?: () => void;
  };
  menu?: MenuItem[];
  mobileExtraLinks?: {
    name: string;
    url: string;
  }[];
  auth?: {
    login: {
      text: string;
      url: string;
      href?: string;
      onClick?: () => void;
    };
    signup: {
      text: string;
      url: string;
      href?: string;
      onClick?: () => void;
    };
  };
}

const Navbar1 = ({
  logo = {
    url: "https://www.shadcnblocks.com",
    src: "https://www.shadcnblocks.com/images/block/block-1.svg",
    alt: "logo",
    title: "Shadcnblocks.com",
  },
  menu = [
    { title: "Home", url: "#" },
    {
      title: "Products",
      url: "#",
      items: [
        {
          title: "Blog",
          description: "The latest industry news, updates, and info",
          icon: <Book className="size-5 shrink-0" />,
          url: "#",
        },
        {
          title: "Company",
          description: "Our mission is to innovate and empower the world",
          icon: <Trees className="size-5 shrink-0" />,
          url: "#",
        },
        {
          title: "Careers",
          description: "Browse job listing and discover our workspace",
          icon: <Sunset className="size-5 shrink-0" />,
          url: "#",
        },
        {
          title: "Support",
          description:
            "Get in touch with our support team or visit our community forums",
          icon: <Zap className="size-5 shrink-0" />,
          url: "#",
        },
      ],
    },
    {
      title: "Resources",
      url: "#",
      items: [
        {
          title: "Help Center",
          description: "Get all the answers you need right here",
          icon: <Zap className="size-5 shrink-0" />,
          url: "#",
        },
        {
          title: "Contact Us",
          description: "We are here to help you with any questions you have",
          icon: <Sunset className="size-5 shrink-0" />,
          url: "#",
        },
        {
          title: "Status",
          description: "Check the current status of our services and APIs",
          icon: <Trees className="size-5 shrink-0" />,
          url: "#",
        },
        {
          title: "Terms of Service",
          description: "Our terms and conditions for using our services",
          icon: <Book className="size-5 shrink-0" />,
          url: "#",
        },
      ],
    },
    {
      title: "Pricing",
      url: "#",
    },
    {
      title: "Blog",
      url: "#",
    },
  ],
  mobileExtraLinks = [
    { name: "Press", url: "#" },
    { name: "Contact", url: "#" },
    { name: "Imprint", url: "#" },
    { name: "Sitemap", url: "#" },
  ],
  auth = {
    login: { text: "Log in", url: "#" },
    signup: { text: "Sign up", url: "#" },
  },
}: Navbar1Props) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
    <section className="fixed top-0 left-0 right-0 z-[99999999] bg-background/80 backdrop-blur-lg border-b border-border py-2">
      <div className="container">
        <nav className="hidden justify-between lg:flex">
          <div className="flex items-center gap-6">
            {logo.onClick ? (
              <div 
                onClick={logo.onClick} 
                className="flex items-center gap-2 cursor-pointer" 
                id="nav-logo"
              >
                {logo.src ? (
                  <img 
                    src={logo.src} 
                    alt={logo.alt} 
                    className="h-6 w-auto"
                  />
                ) : (
                  <span className="text-xl font-bold text-foreground hover:text-[#FE786A] transition-colors">{logo.title}</span>
                )}
              </div>
            ) : (
              <Link to={logo.url} className="flex items-center gap-2" id="nav-logo">
                {logo.src ? (
                  <img 
                    src={logo.src} 
                    alt={logo.alt} 
                    className="h-6 w-auto"
                  />
                ) : (
                  <span className="text-xl font-bold text-foreground hover:text-[#FE786A] transition-colors">{logo.title}</span>
                )}
              </Link>
            )}
            <div className="flex items-center gap-1">
              {menu.map((item) => renderMenuItem(item))}
            </div>
          </div>
          <div className="flex gap-2">
            {auth.signup.text && (
              auth.signup.onClick ? (
                <button 
                  id="nav-signup-button"
                  onClick={auth.signup.onClick}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-white bg-gradient-to-r from-[#2563EB] to-[#9333EA] hover:shadow-lg hover:-translate-y-0.5"
                >
                  {auth.signup.text}
                </button>
              ) : auth.signup.href ? (
                <a 
                  id="nav-signup-link"
                  href={auth.signup.href}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-white bg-gradient-to-r from-[#2563EB] to-[#9333EA] hover:shadow-lg hover:-translate-y-0.5"
                >
                  {auth.signup.text}
                </a>
              ) : (
                <Link 
                  id="nav-signup-link"
                  to={auth.signup.url}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-white bg-gradient-to-r from-[#2563EB] to-[#9333EA] hover:shadow-lg hover:-translate-y-0.5"
                >
                  {auth.signup.text}
                </Link>
              )
            )}
            {auth.login.text && (
              auth.login.onClick ? (
                <button 
                  id="nav-login-button"
                  onClick={auth.login.onClick}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-foreground/80 bg-transparent hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#9333EA] hover:text-white"
                >
                  {auth.login.text}
                </button>
              ) : auth.login.href ? (
                <a 
                  id="nav-login-link"
                  href={auth.login.href}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-foreground/80 bg-transparent hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#9333EA] hover:text-white"
                >
                  {auth.login.text}
                </a>
              ) : (
                <Link 
                  id="nav-login-link"
                  to={auth.login.url}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-foreground/80 bg-transparent hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#9333EA] hover:text-white"
                >
                  {auth.login.text}
                </Link>
              )
            )}
          </div>
        </nav>
        <div className="block lg:hidden">
          <div className="flex items-center justify-between">
            {logo.onClick ? (
              <div 
                onClick={logo.onClick} 
                className="flex items-center gap-2 cursor-pointer" 
                id="mobile-nav-logo"
              >
                {logo.src ? (
                  <img 
                    src={logo.src} 
                    alt={logo.alt} 
                    className="h-6 w-auto"
                  />
                ) : (
                  <span className="text-lg font-semibold">
                    {logo.title}
                  </span>
                )}
              </div>
            ) : (
              <Link to={logo.url} className="flex items-center gap-2" id="mobile-nav-logo">
                {logo.src ? (
                  <img 
                    src={logo.src} 
                    alt={logo.alt} 
                    className="h-6 w-auto"
                  />
                ) : (
                  <span className="text-lg font-semibold">
                    {logo.title}
                  </span>
                )}
              </Link>
            )}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="size-4" />
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto z-[99999999] w-full sm:w-[540px]">
                <SheetHeader>
                  <SheetTitle>
                    {logo.onClick ? (
                      <div 
                        onClick={logo.onClick} 
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        {logo.src ? (
                          <img 
                            src={logo.src} 
                            alt={logo.alt} 
                            className="h-6 w-auto"
                          />
                        ) : (
                          <span className="text-lg font-semibold">
                            {logo.title}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Link to={logo.url} className="flex items-center gap-2">
                        {logo.src ? (
                          <img 
                            src={logo.src} 
                            alt={logo.alt} 
                            className="h-6 w-auto"
                          />
                        ) : (
                          <span className="text-lg font-semibold">
                            {logo.title}
                          </span>
                        )}
                      </Link>
                    )}
                  </SheetTitle>
                </SheetHeader>
                <div className="my-6 flex flex-col gap-6">
                  <Accordion
                    type="single"
                    collapsible
                    className="flex w-full flex-col gap-4"
                  >
                    {menu.map((item) => renderMobileMenuItem(item, closeMobileMenu))}
                  </Accordion>
                  {mobileExtraLinks && mobileExtraLinks.length > 0 && (
                    <div className="border-t py-4">
                      <div className="grid grid-cols-2 justify-start">
                        {mobileExtraLinks.map((link, idx) => (
                          <a
                            key={idx}
                            className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-[#FE786A]"
                            href={link.url}
                          >
                            {link.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    {auth.signup.text && (
                      auth.signup.onClick ? (
                        <button 
                          id="mobile-nav-signup-button"
                          onClick={auth.signup.onClick}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-white bg-gradient-to-r from-[#2563EB] to-[#9333EA] hover:shadow-lg hover:-translate-y-0.5"
                        >
                          {auth.signup.text}
                        </button>
                      ) : auth.signup.href ? (
                        <a 
                          id="mobile-nav-signup-link"
                          href={auth.signup.href}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-white bg-gradient-to-r from-[#2563EB] to-[#9333EA] hover:shadow-lg hover:-translate-y-0.5"
                        >
                          {auth.signup.text}
                        </a>
                      ) : (
                        <Link 
                          id="mobile-nav-signup-link"
                          to={auth.signup.url}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-white bg-gradient-to-r from-[#2563EB] to-[#9333EA] hover:shadow-lg hover:-translate-y-0.5"
                        >
                          {auth.signup.text}
                        </Link>
                      )
                    )}
                    {auth.login.text && (
                      auth.login.onClick ? (
                        <button 
                          id="mobile-nav-login-button"
                          onClick={auth.login.onClick}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-foreground/80 bg-transparent hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#9333EA] hover:text-white"
                        >
                          {auth.login.text}
                        </button>
                      ) : auth.login.href ? (
                        <a 
                          id="mobile-nav-login-link"
                          href={auth.login.href}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-foreground/80 bg-transparent hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#9333EA] hover:text-white"
                        >
                          {auth.login.text}
                        </a>
                      ) : (
                        <Link 
                          id="mobile-nav-login-link"
                          to={auth.login.url}
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 text-foreground/80 bg-transparent hover:bg-gradient-to-r hover:from-[#2563EB] hover:to-[#9333EA] hover:text-white"
                        >
                          {auth.login.text}
                        </Link>
                      )
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>
    <CountdownBanner />
    </>
  );
};

const renderMenuItem = (item: MenuItem) => {
  if (item.items) {
    const hasMany = item.items.length > 6;
    const gridClass = hasMany ? "grid grid-cols-2 gap-x-4" : "";
    const widthClass = hasMany ? "w-[600px]" : "w-80";
    
    return (
      <HoverCard key={item.title} openDelay={0} closeDelay={150}>
        <HoverCardTrigger asChild>
          <button className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium text-foreground/80 hover:text-[#FE786A] transition-colors hover:bg-transparent focus:bg-transparent focus:outline-none">
            {item.title}
            <ChevronDown className="relative top-[1px] ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent 
          align="start" 
          sideOffset={0}
          className={`${widthClass} p-3`}
        >
          <div className={gridClass}>
            {item.items.map((subItem) => (
              <div key={subItem.title}>
                {subItem.onClick ? (
                  <button
                    id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-${subItem.title.toLowerCase().replace(/\s+/g, '-')}-button`}
                    onClick={subItem.onClick}
                    className="flex select-none gap-4 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:text-[#FE786A] border-none bg-transparent cursor-pointer w-full text-left"
                  >
                    <div className="flex-shrink-0 mt-1">
                      {subItem.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-left">
                        {subItem.title}
                      </div>
                      {subItem.description && (
                        <p className="text-sm leading-snug text-muted-foreground text-left">
                          {subItem.description}
                        </p>
                      )}
                    </div>
                  </button>
                ) : subItem.external ? (
                  <a
                    href={subItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex select-none gap-4 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:text-[#FE786A] w-full"
                  >
                    <div className="flex-shrink-0 mt-1">
                      {subItem.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-left" id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-${subItem.title.toLowerCase().replace(/\s+/g, '-')}-link`}>
                        {subItem.title}
                      </div>
                      {subItem.description && (
                        <p className="text-sm leading-snug text-muted-foreground text-left">
                          {subItem.description}
                        </p>
                      )}
                    </div>
                  </a>
                ) : (
                  <Link
                    to={subItem.url}
                    className="flex select-none gap-4 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:text-[#FE786A] w-full"
                  >
                    <div className="flex-shrink-0 mt-1">
                      {subItem.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-left" id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-${subItem.title.toLowerCase().replace(/\s+/g, '-')}-link`}>
                        {subItem.title}
                      </div>
                      {subItem.description && (
                        <p className="text-sm leading-snug text-muted-foreground text-left">
                          {subItem.description}
                        </p>
                      )}
                    </div>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // If item has onClick handler, render as button
  if (item.onClick) {
    return (
      <button
        key={item.title}
        id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-button`}
        onClick={(e) => {
          e.preventDefault();
          item.onClick!();
        }}
        className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-[#FE786A] border-none cursor-pointer"
      >
        {item.title}
      </button>
    );
  }

  // Use Link for regular navigation items
  return (
    <Link
      key={item.title}
      id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-link`}
      to={item.url}
      className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-[#FE786A]"
    >
      {item.title}
    </Link>
  );
};

const renderMobileMenuItem = (item: MenuItem, closeMobileMenu: () => void) => {
  if (item.items) {
    return (
      <AccordionItem key={item.title} value={item.title} className="border-b-0" id={`mobile-nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
        <AccordionTrigger className="py-0 font-semibold hover:no-underline">
          {item.title}
        </AccordionTrigger>
        <AccordionContent className="mt-2">
          {item.items.map((subItem) => (
            subItem.onClick ? (
              <button
                key={subItem.title}
                id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-${subItem.title.toLowerCase().replace(/\s+/g, '-')}-button`}
                onClick={() => {
                  subItem.onClick!();
                  closeMobileMenu();
                }}
                className="flex select-none gap-4 rounded-md p-3 leading-none outline-none transition-colors hover:text-[#FE786A] border-none bg-transparent cursor-pointer w-full text-left "
              >
                <div className="flex-shrink-0 mt-1">
                  {subItem.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-left">{subItem.title}</div>
                  {subItem.description && (
                    <p className="text-sm leading-snug text-muted-foreground text-left">
                      {subItem.description}
                    </p>
                  )}
                </div>
              </button>
            ) : subItem.external ? (
              <a
                key={subItem.title}
                href={subItem.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex select-none gap-4 rounded-md p-3 leading-none outline-none transition-colors hover:text-[#FE786A]  w-full"
              >
                <div className="flex-shrink-0 mt-1">
                  {subItem.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-left">{subItem.title}</div>
                  {subItem.description && (
                    <p className="text-sm leading-snug text-muted-foreground text-left">
                      {subItem.description}
                    </p>
                  )}
                </div>
              </a>
            ) : (
              <Link
                key={subItem.title}
                id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-${subItem.title.toLowerCase().replace(/\s+/g, '-')}-link`}
                to={subItem.url}
                onClick={closeMobileMenu}
                className="flex select-none gap-4 rounded-md p-3 leading-none outline-none transition-colors hover:text-[#FE786A]  w-full"
              >
                <div className="flex-shrink-0 mt-1">
                  {subItem.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-left">{subItem.title}</div>
                  {subItem.description && (
                    <p className="text-sm leading-snug text-muted-foreground text-left">
                      {subItem.description}
                    </p>
                  )}
                </div>
              </Link>
            )
          ))}
        </AccordionContent>
      </AccordionItem>
    );
  }

  // If item has onClick handler, render as button
  if (item.onClick) {
    return (
      <button 
        key={item.title} 
        id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-button`}
        onClick={(e) => {
          e.preventDefault();
          item.onClick!();
          closeMobileMenu();
        }}
        className="font-semibold text-left border-none bg-transparent p-0 cursor-pointer hover:text-[#FE786A] transition-colors"
      >
        {item.title}
      </button>
    );
  }

  // Use Link for regular navigation items
  return (
    <Link key={item.title} id={`nav-menu-${item.title.toLowerCase().replace(/\s+/g, '-')}-link`} to={item.url} onClick={closeMobileMenu} className="font-semibold">
      {item.title}
    </Link>
  );
};

export { Navbar1 };
